import { getCurrentInstance, onMounted, ref, type Ref } from 'vue'

import { getEventForm } from '@/services/events/eventFormService'
import { handleRequest } from '@/utils/handleRequest';

interface CacheEntry {
  eventForm: Ref<any>
  loaded: boolean
  inFlight: Promise<any> | null
}

// Shared per eventFormId so the show/edit route guards' fetch is reused by
// EventFormEditor on mount instead of refetching the same record.
const cache = new Map<string, CacheEntry>()

function entryFor(eventFormId: string): CacheEntry {
  let entry = cache.get(eventFormId)
  if (!entry) {
    entry = { eventForm: ref(null), loaded: false, inFlight: null }
    cache.set(eventFormId, entry)
  }
  return entry
}

export function useEventFormPolicy(eventFormId: string | number) {

  const entry = entryFor(String(eventFormId))
  const eventForm = entry.eventForm

  // Route guards (show/edit) call fetchEventForm() and get a fresh result on
  // every navigation; the component mounted behind a guard reads the cached
  // copy via { force: false } so the page load costs a single request.
  const fetchEventForm = async ({ force = true } = {}) => {
    if (!force && entry.loaded) return { success: true, data: { event_form: eventForm.value } }
    if (entry.inFlight) return await entry.inFlight

    entry.inFlight = handleRequest(
      () => getEventForm(eventFormId),
      {
        onSuccess: (response: any) => {
          eventForm.value = response.data?.event_form
          entry.loaded = true
        }
      }
    )

    try {
      return await entry.inFlight
    } finally {
      entry.inFlight = null
    }
  }

  const canView = () => eventForm.value?.policies?.show ?? false
  const canEdit = () => eventForm.value?.policies?.update ?? false

  // Auto-fetch only when used inside a component; route guards call
  // fetchEventForm() explicitly and have no instance to mount against.
  if (getCurrentInstance()) {
    onMounted(async () => await fetchEventForm({ force: false }))
  }

  return {
    eventForm,
    fetchEventForm,
    canView,
    canEdit
  }
}
